import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PAGE } from "../constants/page";
import "../styles/components/Header.scss";
import ProfileMenu from "./ProfileMenu";
import { isBrowser, isMobile } from "react-device-detect";
import AuthService from "../services/AuthService";
import ConfirmationModal from "./ConfirmationModal";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { patientName, updatedPatient } from "../redux/reducers/PatientSlice";
import {
  updatePageLoad,
  UserState,
  hideSuccessMessage,
} from "../redux/reducers/UserSlice";
import {
  updatedAsha,
} from "src/redux/reducers/AuthAshaDetailsSlice";
import { PersistState, updateLanguage } from "../redux/reducers/PersistSlice";
import { UserConfigState } from "../redux/reducers/UserConfigSlice";
import { persistor } from "../redux/store";
import ErrorBoundaryHandler from "./ErrorBoundaryHandler";
import HeaderLogo from "./HeaderLogo";
import { Dropdown } from "@els/els-react--dropdown";

type Props = {
  auth: any;
};

function Header(props: Props) {
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { i18n } = useTranslation();
  const firstName = props.auth?.user?.profile.given_name || "";
  const lastName = props.auth?.user?.profile.family_name || "";
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userDetails: any = useAppSelector(UserState);
  const userSelectedLanguage: any = useAppSelector(PersistState);
  const userConfigDetails: any = useAppSelector(UserConfigState);
  const [displayHelp, setDisplayHelp] = useState(false);
  const positionClass = isMobile ? "header-position-initial" : "header-position-relative";

  const handleLogout = () => {
    dispatch(updatedAsha({ updated: false }));
    setShowConfirmationModal(false);
    AuthService.instance.logout(userSelectedLanguage?.language || "en");
  };

  // HandleChange for Leyden Select Component
  const handleLanguageChange = (e: any) => {
    i18n.changeLanguage(e.id);
    dispatch(updateLanguage(e.id));
    dispatch(hideSuccessMessage(true));
    dispatch(updatedAsha({ updated: false }));
  };

  const handeUserInitialsClick = () => {
    setShowMenu(!showMenu);
    dispatch(updatedAsha({ updated: false }));
    dispatch(hideSuccessMessage(true));
  };

  const handleClickOutside = () => {
    setShowMenu(false);
  };

  const handleHelp = () => {
    dispatch(updatedAsha({ updated: false }));
    setDisplayHelp(false);
  };

  const handleBackButtonEvent = () => {
    setShowMenu(false);
    setDisplayHelp(false);
    setShowConfirmationModal(false);
  };

  useEffect(() => {
    window.addEventListener("popstate", handleBackButtonEvent);

    return () => {
      window.removeEventListener("popstate", handleBackButtonEvent);
    };
  }, []);

  const renderProfileIcon = () => {
    return (
      <button
        className={`userInitials ${showMenu ? "open" : ""}`}
        onClick={handeUserInitialsClick}
      >
        {`${firstName?.charAt(0)}${lastName?.charAt(0)}`}
      </button>
    );
  };

  return location.pathname == "/screening" ? null : (
    <div className="header">
      <div className={`container ${positionClass}`}>
        <div className="row">
          <div
            className="col-md-8 col-10 text-start"
            style={{
              height: !isMobile ? "50px" : "48px",
            }}
          >
            <Link
              to="home"
              id="homeLink"
              onClick={e => {
                e.preventDefault();
                navigate(PAGE.GLOBAL_HOME);
                dispatch(updatedPatient(false));
                setShowMenu(false);
                dispatch(patientName({ firstName: null, lastName: null }));
                dispatch(updatePageLoad(false));
              }}
            >
              <HeaderLogo brand={userSelectedLanguage.brand}/>
            </Link>
          </div>
          <div className="col-md-4 col-2 text-end menu-container">
            {isBrowser ? (
              <div className="d-inline-block select-language me-3">
                <Dropdown
                  selected={{
                    label:
                    userConfigDetails?.countryLanguageOptions.filter((langLabelValueObj: any) => {
                        return (
                          langLabelValueObj.id ===
                          userSelectedLanguage?.language
                        );
                      })[0]?.label ?? "English",
                  }}
                  showSelectedAsLabel={true}
                  id="language-select"
                  options={userConfigDetails?.countryLanguageOptions.map((options: {}) => {
                    return {
                      ...options,
                      onClick: handleLanguageChange,
                    };
                  })}
                  flyoutProps={{
                    placement: "bottom-end",
                    pointer: false,
                    pointerType: "primary",
                  }}
                  className="textTransform"
                />
              </div>
            ) : null}
            {isMobile ? (
              <>
                {showMenu ? (
                  <button
                    className="menu-btn-mobile"
                    onClick={handeUserInitialsClick}
                  >
                    <img width={26} height={16} src="/icons/close.svg" />
                  </button>
                ) : (
                  <button
                    className="menu-btn-mobile"
                    onClick={handeUserInitialsClick}
                  >
                    {" "}
                    <img width={24} height={24} src="/icons/hamburger.svg" />
                  </button>
                )}
              </>
            ) : (
              renderProfileIcon()
            )}

            {showMenu && (
              <ProfileMenu
                firstName={firstName}
                lastName={lastName}
                onClickOutside={handleClickOutside}
                onLogout={() => setShowConfirmationModal(true)}
                onHelp={() => setDisplayHelp(true)}
                userLanguageSelected={userSelectedLanguage?.language}
                // handleLanguageChange={handleChange}
                handleLanguageChange={handleLanguageChange}
                userType={
                  userDetails?.userDetails?.app_metadata?.organizations?.EODB
                    ?.groups[0]
                }
              />
            )}
          </div>
        </div>
      </div>
      {showConfirmationModal && (
        <ConfirmationModal
          onCancel={() => setShowConfirmationModal(false)}
          onYes={handleLogout}
          onClose={() => setShowConfirmationModal(false)}
          messageKey="logout_confirmation"
          titleKey="logout"
          yesKey="logout"
          cancelkey="cancel"
          isCenter={true}
          language={userSelectedLanguage?.language || "en"}
        />
      )}
      {displayHelp && (
        <ConfirmationModal
          onCancel={() => setDisplayHelp(false)}
          onYes={handleHelp}
          onClose={() => setDisplayHelp(false)}
          messageKey="help_message"
          titleKey="help"
          yesKey="close"
          hideNoButton={true}
          isCenter={true}
          language={userSelectedLanguage?.language || "en"}
        />
      )}
    </div>
  );
}

export default ErrorBoundaryHandler(Header);
